import { useLazyQuery } from '@apollo/client';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Avatar,
  AvatarFallback,
  Combobox,
  ComboboxDropdown,
  ComboboxSelectButton,
  Icon,
  Stack,
} from '@eluve/components';
import { useCancellableSearch } from '@eluve/utility-hooks';

import { PatientComboboxContent } from './PatientComboboxContent';
import { PatientOption } from './PatientOption';
import { patientOptionFragment, searchPatientsQuery } from './operations';

interface TenantPatientPickerProps {
  disabled?: boolean;
  tenantId: string;
  /** Controlled selection — the parent owns the chosen patient id. */
  selectedPatientId?: string;
  onPatientSelected: (patientId?: string) => void;
}

/**
 * Searchable tenant patient picker for surfaces with NO appointment context
 * (e.g. the tenant Tasks view). A thin CONTROLLED shell over the shared
 * {@link PatientComboboxContent} + `searchPatients` query — the appointment-
 * free counterpart of {@link PatientSelector}, whose selection is instead
 * derived from an AppointmentContextProvider. Only the Combobox shell differs;
 * all search / option / new-patient logic is reused.
 */
export const TenantPatientPicker: React.FC<TenantPatientPickerProps> = ({
  tenantId,
  selectedPatientId,
  onPatientSelected,
  disabled = false,
}) => {
  const [searchPatients, { loading }] = useLazyQuery(searchPatientsQuery);

  // Debounced tenant patient search with in-flight cancellation, so a
  // superseded or post-reset response can't repopulate stale results. The
  // signal threads into Apollo's fetchOptions to cancel at the network layer.
  const { searchResults, debouncedSearch, resetSearchResults } =
    useCancellableSearch({
      execute: (query, signal) =>
        searchPatients({
          variables: { query, limit: 25, tenantId },
          context: { fetchOptions: { signal } },
        }).then((result) => result.data?.searchPatients ?? []),
    });

  // The chosen patient's details ride in the Apollo cache off the search
  // results (each carries the PatientOption fragment), so the controlled id
  // resolves to a full option with no dedicated fetch.
  const selectedPatient = useCompleteFragment({
    fragment: patientOptionFragment,
    key: { id: selectedPatientId ?? '' },
    strict: false,
  });

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton className="w-full px-4 hover:bg-bg-quaternary">
        {selectedPatient ? (
          <PatientOption {...selectedPatient} />
        ) : (
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            <Avatar size="lg">
              <AvatarFallback>
                <Icon name="ContactRound" />
              </AvatarFallback>
            </Avatar>
            <span className="text-text-secondary">Select Patient</span>
          </Stack>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown
        className="pointer-events-auto"
        searchPlaceholder="Search by name, phone, or email"
        shouldFilter={false}
      >
        <PatientComboboxContent
          selectedPatient={selectedPatient}
          searchResults={searchResults}
          loading={loading}
          handlePatientSelected={onPatientSelected}
          tenantId={tenantId}
          debouncedSearch={debouncedSearch}
          setSearchResults={resetSearchResults}
        />
      </ComboboxDropdown>
    </Combobox>
  );
};
